<template>
  <InfusePageLayout>
    <div class="flex flex-col gap-6 overflow-y-auto">
      <TenantsTable
        :rows="tenants"
        :active="(row, _) => route?.params?.id?.toString() === row.id"
        :loading="isLoading || isDeletingTenant"
        :page="currentPage"
        :searchEnabled="true"
        :searchQuery="searchQuery"
        :key="actionsCompleted"
        :has-more-items="hasNextPage"
        :removable="true"
        :editable="true"
        @load="fetchNextPage"
        @search="search($event)"
        @create="showCreateTenantModal = true"
        @remove="
          (row) => {
            activeTenant = row;
            showDeleteTenantModal = true;
          }
        "
        @click-row="
          (row) => {
            router.push({
              name: 'tenant.details',
              params: { id: row.id }
            });
          }
        "
        @edit="
          (row) => {
            activeTenant = row;
            showUpdateTenantModal = true;
          }
        "
      />
    </div>
  </InfusePageLayout>

  <!-- CREATE -->
  <MutationTenantModal
    v-if="showCreateTenantModal"
    @close="showCreateTenantModal = false"
    @mutate="onCreateTenant"
    :loading="isCreatingTenant"
  />

  <!-- UPDATE -->
  <MutationTenantModal
    v-if="showUpdateTenantModal && activeTenant"
    :tenant="activeTenant"
    :loading="isUpdatingTenant"
    @close="closeUpdateModal"
    @mutate="onUpdateTenant"
  />

  <InfuseConfirmModal
    v-if="showDeleteTenantModal"
    :action-title="t('iam.tenants.header.delete_tenant')"
    :action-description="t('iam.tenants.info.delete_tenant', { tenant: activeTenant?.name || '' })"
    verification-text="delete"
    @confirm="onDeleteTenant"
    @close="closeDeleteModal"
  />
</template>

<script setup lang="ts">
import { InfusePageLayout } from '@infuse/ui/layouts';
import { ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useTenants } from '@infuse/iam/tenant/queries';
import { useCreateTenant, useDeleteTenant, useUpdateTenant } from '@infuse/iam/tenant/mutations';
import { CreateTenant, UpdateTenant, Tenant } from '@infuse/api/iam/tenant';
import { InfuseConfirmModal } from '@infuse/ui/components';
import { useI18n } from 'vue-i18n';
import { MutationTenantModal, TenantsTable } from '@infuse/iam/tenant/ui';

defineEmits(['reloadModel']);

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

const activeTenant = ref<Tenant | undefined>(undefined);

const { tenants, currentPage, isLoading, hasNextPage, fetchNextPage, searchQuery } = useTenants();
const { mutateAsync: createTenant, isPending: isCreatingTenant } = useCreateTenant();
const { mutateAsync: updateTenant, isPending: isUpdatingTenant } = useUpdateTenant();
const { mutateAsync: deleteTenant, isPending: isDeletingTenant } = useDeleteTenant();

const actionsCompleted = ref(0);
const showCreateTenantModal = ref(false);
const showUpdateTenantModal = ref(false);
const showDeleteTenantModal = ref(false);

watch(
  () => route.query.search,
  (search) => {
    searchQuery.value = search?.toString();
  },
  { immediate: true }
);

async function search(search: string): Promise<void> {
  searchQuery.value = search;
  replaceQueryParams(searchQuery.value);
}

function replaceQueryParams(searchQuery: string | undefined) {
  if (router.currentRoute.value.name !== 'tenant') {
    return;
  }
  router.replace({
    name: 'tenant',
    query: {
      search: searchQuery || undefined
    }
  });
}

async function onCreateTenant(vars: { tenant: CreateTenant }): Promise<void> {
  await createTenant(vars.tenant);
  showCreateTenantModal.value = false;
}

async function onDeleteTenant(): Promise<void> {
  if (!activeTenant.value) {
    return;
  }
  await deleteTenant(activeTenant.value.id);
  closeDeleteModal();
}

function closeDeleteModal() {
  showDeleteTenantModal.value = false;
  activeTenant.value = undefined;
}

async function onUpdateTenant(vars: { id: string; tenant: UpdateTenant }): Promise<void> {
  await updateTenant(vars);
  closeUpdateModal();
}

function closeUpdateModal() {
  showUpdateTenantModal.value = false;
  activeTenant.value = undefined;
}
</script>
